import loadable, { type LoadableComponent } from '@loadable/component';
import React, { useContext, useState } from 'react';
import classNames from 'classnames/bind';
import { useQuery } from '@apollo/client';
import { detect } from 'detect-browser';
import { useRouteMatch } from 'react-router-dom';
import { dashSpaceSwap } from '../../../utils/url-search-helpers';
import { ORGANIZATION_HERO_QUERY, NATIONAL_SLIDE_QUERY } from './HOMEPAGE_QUERIES';
import { OrganizationsContext } from '../../../contexts';
import { PageLoader, VPAdBanner } from '../../../components';
import { Header } from './Header';
import Hero from './Hero';
import PlayItForward from './PlayItForward';
import VoloApp from './VoloApp';
import Footer from './Footer';
import UseApp from './UseApp';
import styles from './styles.module.scss';
import { StaticUrls } from '../../../shared/static-urls-enum';
import useKidsFoundationStats from '../../../hooks/useKidsFoundationStats';

const cx = classNames.bind(styles);

const appURLs = {
  iOS: StaticUrls.IOS,
  'Android OS': StaticUrls.ANDROID,
};

const BrandLogosBanner = loadable(() => import('./BrandLogosBanner'));
const WhyShouldIPlay = loadable(() => import('./WhyShouldIPlay'));
const WaysToPlay = loadable(() => import('./WaysToPlay'));

// Type cast here because `loadable/component` isn't correctly inferring the props from prop-types
const UpcomingEvents = loadable(() => import('./UpcomingEvents')) as LoadableComponent<{
  cityName: string;
  chosenSport?: string;
}>;

const SignUpForLeague = loadable(() => import('./SignUpForLeague'));
const WhereWePlay = loadable(() => import('./WhereWePlay/WhereWePlay'));
const LeaguePerks = loadable(() => import('./LeaguePerks'));
const Mission = loadable(() => import('./Mission'));
const WaysToPlayWithUs = loadable(() => import('./WaysToPlayWithUs'));

const HomePage: React.FC = () => {
  const { path } = useRouteMatch();

  const cityName = dashSpaceSwap(path.substring(1));

  // handles mobile-only popup if viewing on mobile
  const detected = detect()?.os ?? '';
  const url = detected in appURLs ? appURLs[detected as keyof typeof appURLs] : '';
  const [chosenView, setChosenView] = useState('');

  const organizationData = useContext(OrganizationsContext);
  const selectedOrg = organizationData?.find(({ name }) => name === cityName);
  const cityOrgId = selectedOrg?._id;

  // fetches hero slides data based on current selected organization
  let heroSlides;
  let slideDuration;

  const { data: kidsFoundationData, loading: kidsFoundationLoading } = useKidsFoundationStats();

  const { data: organizationHeroData, loading: organizationHeroLoading } = useQuery(
    ORGANIZATION_HERO_QUERY,
    {
      skip: !cityOrgId,
      variables: {
        organizationId: cityOrgId,
      },
    }
  );

  if (organizationHeroData) {
    heroSlides = organizationHeroData?.organization?.heroSlides;
    slideDuration = organizationHeroData?.organization?.slideDuration;
  }

  // if no organization is selected or if org has no slides, fetches national hero slides
  const { data: nationalHeroData, loading: nationalHeroLoading } = useQuery(NATIONAL_SLIDE_QUERY, {
    skip: heroSlides?.length > 0,
  });

  if (nationalHeroData) {
    heroSlides = nationalHeroData?.nationalSlides?.heroSlides;
    slideDuration = nationalHeroData?.nationalSlides?.slideDuration;
  }

  if (organizationHeroLoading || nationalHeroLoading || kidsFoundationLoading)
    return <PageLoader />;

  const { numberOfKidsEngaged, programsCompleted, numberOfVolunteersMobilized, citiesActivated } =
    kidsFoundationData!.kidsFoundationStats;

  return (
    <>
      <div className={cx('App', 'homepage', !!url && !chosenView && 'queryApp')}>
        <Header />
        <Hero
          cityName={cityName}
          heroSlides={heroSlides || []}
          slideDuration={slideDuration || 5000}
        />
        {!cityName ? ( // show national page
          <>
            <Mission />
            <WaysToPlayWithUs />
            <PlayItForward
              kidsEngaged={numberOfKidsEngaged ?? 0}
              programsCompleted={programsCompleted ?? 0}
              volunteersMobilized={numberOfVolunteersMobilized ?? 0}
              citiesActivated={citiesActivated ?? 0}
            />
            <WhereWePlay />
            <VoloApp />
          </>
        ) : (
          // show city specific page
          <>
            <BrandLogosBanner cityName={cityName} />
            <WaysToPlay cityName={cityName} />
            <WhyShouldIPlay />
            <UpcomingEvents cityName={cityName} />
            <SignUpForLeague cityName={cityName} />
            <PlayItForward
              kidsEngaged={numberOfKidsEngaged ?? 0}
              programsCompleted={programsCompleted ?? 0}
              volunteersMobilized={numberOfVolunteersMobilized ?? 0}
              citiesActivated={citiesActivated ?? 0}
            />
            <LeaguePerks />
            <VoloApp />
          </>
        )}
        <Footer />
      </div>
      {!!url && !chosenView && <UseApp url={url} setChosenView={setChosenView} />}
      <VPAdBanner
        href={cityName ? `/${dashSpaceSwap(cityName, 'toDash')}/volo-pass` : '/volo-pass'}
      />
    </>
  );
};

export default HomePage;
